import { useEffect, useMemo, useState } from 'react';
import type { TimetableSlot } from '@/types/timetable';
import { Navigate, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Search,
  Calendar,
  CheckCircle2,
  Clock,
  FileText,
  Lightbulb,
  MapPin,
  Plus,
  StickyNote,
  Users,
  Video,
  X,
} from 'lucide-react';

import { PageHeader } from '@/components/PageHeader';
import { DatePicker } from '@/components/ui/DatePicker';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { EmptyState } from '@/components/ui/EmptyState';
import { Select } from '@/components/ui/Select';
import { SkeletonLines } from '@/components/ui/Skeleton';
import { StatCard } from '@/components/ui/StatCard';
import type { Option } from '@/constants/institute';
import { teacherNav, navLabel } from '@/constants/navigation';
import { ROLE_HOME, ROLES, TEACHING_ROLES } from '@/constants/roles';
import { tipForDay } from '@/constants/teachingTips';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { useInstituteBranding } from '@/features/institute/store/branding.store';
import { dailyReportsApi } from '@/features/teacher/api/dailyReports.api';
import { ClassSelectorHeader } from '@/features/teacher/components/ClassSelectorHeader';
import { BestPerformersCard } from '@/features/teacher/components/dashboard/BestPerformersCard';
import { StudentProgressCard } from '@/features/teacher/components/dashboard/StudentProgressCard';
import { TeacherActivitiesSection } from '@/features/teacher/components/TeacherActivitiesSection';
import { TeacherAttendanceSection } from '@/features/teacher/components/TeacherAttendanceSection';
import { TeacherAttendanceAnalyticsCard } from '@/features/teacher/components/attendance/TeacherAttendanceAnalyticsCard';
import { InstituteNoticesCard } from '@/features/teacher/components/dashboard/InstituteNoticesCard';
import { MyClassesCard } from '@/features/teacher/components/dashboard/MyClassesCard';
import { TeacherClassNotesCard } from '@/features/teacher/components/dashboard/TeacherClassNotesCard';
import { TeacherNoticesCard } from '@/features/teacher/components/dashboard/TeacherNoticesCard';
import { TeacherHomeworkSection } from '@/features/teacher/components/TeacherHomeworkSection';
import { TeacherMarksSection } from '@/features/teacher/components/TeacherMarksSection';
import { TeacherTimetableSection } from '@/features/teacher/components/TeacherTimetableSection';
import { periodRange } from '@/features/teacher/components/timetable/timetableCopy';
import { TeacherVideosSection } from '@/features/teacher/components/TeacherVideosSection';
import { teacherApi } from '@/features/teacher/api/teacher.api';
import { useHeaderClass } from '@/features/teacher/store/headerClass.store';
import type { AllocatedClass, TaughtClass } from '@/features/teacher/types/teacher';
import {
  allotmentLabel,
  distinctClassNames,
  distinctSections,
  formatInstituteDate,
  formatInstituteDateTime,
  formatInstituteLongDate,
  instituteMinutesNow,
  instituteToday,
  isTaughtClass,
  toAllocatedClass,
} from '@/features/teacher/utils';
import { useApiQuery } from '@/hooks/useApiQuery';
import { toast } from '@/hooks/useToast';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { ApiRequestError } from '@/lib/apiClient';
import { capitalizeWords } from '@/lib/format';

const EMPTY_ALLOCATED_CLASS: AllocatedClass = {
  id: '',
  classSectionUuid: '',
  className: 'No class allotted',
  grade: '',
  section: '',
  subject: '',
  subjectUuid: '',
  role: 'Subject Teacher',
  totalStudents: 0,
};

const EMPTY_TAUGHT_CLASS: TaughtClass = {
  ...EMPTY_ALLOCATED_CLASS,
  subject: '',
  subjectUuid: '',
};

/** Where the day being looked at sits relative to today. */
type DayState = 'past' | 'today' | 'future';

/** "08:30" → minutes since midnight, for placing a period against the clock. */
function toMinutes(clockTime: string): number {
  const [hours, minutes] = clockTime.split(':').map(Number);
  return hours * 60 + minutes;
}

/**
 * What one period on the schedule says it is.
 *
 * A period the teacher has excused themselves from says so whatever the clock
 * reads — the timetable already carries that, and a screen calling it "in
 * progress" would be telling them they are somewhere they are not.
 */
function slotStatus(slot: TimetableSlot, dayState: DayState, minutesNow: number): string {
  if (slot.availability.status === 'UNAVAILABLE') return 'Unavailable';
  if (dayState === 'past') return 'Completed';
  if (dayState === 'future') return 'Scheduled';

  if (minutesNow < toMinutes(slot.period.startTime)) return 'Upcoming';
  if (minutesNow <= toMinutes(slot.period.endTime)) return 'In Progress';

  return 'Completed';
}

/**
 * The homework search, which sits in this screen's header rather than in the
 * homework section, kept in the address bar so reviewing a piece of work and
 * coming back finds it still typed. Prefixed, so it is never read as the
 * search of another section this screen hosts.
 */
type HomeworkSearchFilters = { search: string };

const HOMEWORK_SEARCH_DEFAULTS: HomeworkSearchFilters = { search: '' };

const HOMEWORK_SEARCH_OPTIONS = { prefix: 'homework' };

/** The tabs that work on one subject — a register-only class has none to offer. */
const SUBJECT_TABS: readonly string[] = ['homework', 'activities', 'videos', 'marks'];

export function DashboardPage() {
  const user = useAuthStore((state) => state.user);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const institutionType = useInstituteBranding((state) => state.branding?.institutionType);

  // The sections as this institute names them: a college's teacher reaches the
  // same screen at /teacher/assignments, and the page titles itself to match.
  const sections = useMemo(() => teacherNav(institutionType), [institutionType]);

  // Derive active tab from location state, search query parameter, or URL path
  const getActiveTab = ():
    'overview' | 'attendance' | 'homework' | 'activities' | 'videos' | 'timetable' | 'marks' => {
    const tabFromState = location.state?.activeTab;
    if (
      tabFromState &&
      ['overview', 'attendance', 'homework', 'activities', 'videos', 'timetable', 'marks'].includes(
        tabFromState,
      )
    ) {
      return tabFromState as any;
    }

    const tabParam = searchParams.get('tab');
    if (
      tabParam &&
      ['overview', 'attendance', 'homework', 'activities', 'videos', 'timetable', 'marks'].includes(
        tabParam,
      )
    ) {
      return tabParam as any;
    }

    const path = location.pathname;
    if (path.includes('/attendance')) return 'attendance';
    // Two addresses, one screen: a college calls this work assignments.
    if (path.includes('/homework') || path.includes('/assignments')) return 'homework';
    if (path.includes('/activities')) return 'activities';
    if (path.includes('/videos')) return 'videos';
    if (path.includes('/timetable')) return 'timetable';
    if (path.includes('/marks')) return 'marks';

    return 'overview';
  };

  const activeTab = getActiveTab();

  const setActiveTab = (
    tab: 'overview' | 'attendance' | 'homework' | 'activities' | 'videos' | 'timetable' | 'marks',
  ) => {
    // The address the sidebar uses for that section, so a quick action and the
    // link beside it never land on different paths.
    const targetPath = sections.find((section) => section.key === tab)?.to ?? '/teacher/dashboard';
    navigate(targetPath, { state: { activeTab: tab } });
  };

  // When arriving from the dashboard "View all" link:
  // 1. The browser scrolls to top first so the user sees they've changed page.
  // 2. After a short pause we smoothly guide them down to the target section.
  useEffect(() => {
    const scrollTo = location.state?.scrollTo as string | undefined;
    if (!scrollTo) return;

    let rafId: number;

    const delayTimer = setTimeout(() => {
      let attempts = 0;
      const maxAttempts = 120; // ~2 s at 60 fps

      const tryScroll = () => {
        const el = document.getElementById(scrollTo);
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (++attempts < maxAttempts) {
          rafId = requestAnimationFrame(tryScroll);
        }
      };

      rafId = requestAnimationFrame(tryScroll);
    }, 350);

    return () => {
      clearTimeout(delayTimer);
      cancelAnimationFrame(rafId);
    };
  }, [location.state]);

  /**
   * The sections and subjects the office has allotted to this teacher.
   *
   * Everything below hangs off it — the class selector, every tab's header, and
   * the overview's filters. Resolved from the caller's own token, so there is
   * no id here for anybody to point at somebody else's classes.
   */
  // This screen also stands in for the roles that have no console of their own
  // yet. Only teaching staff can hold an allotment, so for anybody else the
  // request is skipped rather than sent to be answered with nothing.
  const canHoldClasses = Boolean(user && TEACHING_ROLES.includes(user.role));

  const {
    data: allocations,
    isLoading: isLoadingClasses,
    error: classesError,
    refetch: refetchClasses,
  } = useApiQuery(
    () => (canHoldClasses ? teacherApi.myAllocations() : Promise.resolve([])),
    [canHoldClasses],
  );

  const allocatedClasses: AllocatedClass[] = useMemo(
    () => (allocations ?? []).map(toAllocatedClass),
    [allocations],
  );

  /** One entry per section — the same section can carry two of their subjects. */
  const overviewClassOptions = useMemo(
    () => distinctClassNames(allocatedClasses),
    [allocatedClasses],
  );

  const overviewClassFilterOptions = useMemo<Option[]>(
    () => [
      { value: 'all', label: `All Classes (${overviewClassOptions.length})` },
      ...overviewClassOptions.map((className) => ({ value: className, label: className })),
    ],
    [overviewClassOptions],
  );

  // Seeded from `?class=`, which the recorded-classes library writes so that
  // coming back from a lesson keeps the class it was showing. An id that is not
  // one of this teacher's allotments matches nothing and falls back below.
  const [selectedClassId, setSelectedClassId] = useState<string>(
    () => searchParams.get('class') ?? '',
  );
  // Nothing is chosen until the list arrives; the first allotment then stands
  // in, and a class that disappears from it falls back the same way.
  const currentClass =
    allocatedClasses.find((c) => c.id === selectedClassId) ?? allocatedClasses[0] ?? EMPTY_ALLOCATED_CLASS;

  // Homework, marks, videos and activities are set for a subject, so a section
  // they only run the register of is left out of those tabs.
  const taughtClasses = useMemo(() => allocatedClasses.filter(isTaughtClass), [allocatedClasses]);
  const classTeacherSections = useMemo(
    () => allocatedClasses.filter((c) => c.role === 'Class Teacher'),
    [allocatedClasses],
  );
  const currentTaughtClass =
    taughtClasses.find((c) => c.id === selectedClassId) ?? taughtClasses[0] ?? EMPTY_TAUGHT_CLASS;

  const homeworkClasses = taughtClasses.length > 0 ? taughtClasses : allocatedClasses;
  const homeworkClassOptions = useMemo<Option[]>(
    () =>
      homeworkClasses.map((ac) => ({
        value: ac.id,
        label: allotmentLabel(ac),
      })),
    [homeworkClasses],
  );

  useEffect(() => {
    if (!selectedClassId && taughtClasses.length > 0) {
      setSelectedClassId(taughtClasses[0].id);
    }
  }, [selectedClassId, taughtClasses]);

  /**
   * The class the top bar names, on the tabs whose Active Class picker decides
   * what the screen shows — with the subjects they take in that class only, so
   * the top bar and the subject dropdown below it say the same thing. The
   * overview and the marks tab filter on their own, so they leave the top bar
   * listing every subject.
   */
  const setHeaderClass = useHeaderClass((state) => state.setLabel);
  const headerClass =
    activeTab === 'attendance' || activeTab === 'timetable'
      ? currentClass
      : activeTab === 'homework' || activeTab === 'activities' || activeTab === 'videos'
        ? currentTaughtClass
        : null;
  const headerClassLabel = useMemo(() => {
    if (!headerClass?.classSectionUuid) return null;
    const subjects = [
      ...new Set(
        allocatedClasses
          .filter((c) => c.classSectionUuid === headerClass.classSectionUuid && c.subject)
          .map((c) => capitalizeWords(c.subject ?? '')),
      ),
    ];
    return `${headerClass.className} · ${subjects.length > 0 ? subjects.join(', ') : 'Class teacher'}`;
  }, [headerClass, allocatedClasses]);

  useEffect(() => {
    setHeaderClass(headerClassLabel);
  }, [headerClassLabel, setHeaderClass]);

  // Leaving the dashboard for a screen with no class picker puts the full list back.
  useEffect(() => () => setHeaderClass(null), [setHeaderClass]);

  // The institute's day, in IST — `toISOString` is UTC, which before half past
  // five in the morning is still yesterday.
  const todayDateString = instituteToday();
  const [overviewClassFilter, setOverviewClassFilter] = useState<string>('all');
  const [overviewDateFilter, setOverviewDateFilter] = useState<string>(todayDateString);
  const [isHomeworkFormOpen, setIsHomeworkFormOpen] = useState(false);
  const [homeworkSearch, setHomeworkSearch] = useUrlFilters(
    HOMEWORK_SEARCH_DEFAULTS,
    HOMEWORK_SEARCH_OPTIONS,
  );
  const homeworkSearchQuery = homeworkSearch.search;

  /**
   * Every figure on this screen for the day being looked at.
   *
   * One request rather than one per card: the tiles, the schedule and the two
   * performance cards are all questions about the same allotments, and asking
   * separately would let them answer for different days.
   */
  const {
    data: overview,
    isLoading: isLoadingOverview,
    error: overviewError,
    refetch: refetchOverview,
  } = useApiQuery(
    () =>
      // Only while the overview is on screen. It totals a term of marks and six
      // registers; the other six tabs fetch their own and would be paying for
      // this one for nothing.
      canHoldClasses && activeTab === 'overview'
        ? teacherApi.overview(overviewDateFilter)
        : Promise.resolve(null),
    [canHoldClasses, activeTab, overviewDateFilter],
  );

  // Teaching tip banner state
  const [showTeachingTip, setShowTeachingTip] = useState<boolean>(true);
  const teachingTip = tipForDay(overviewDateFilter);

  const greeting = useMemo(() => {
    const currentHour = new Date().getHours();
    if (currentHour < 12) return 'Good Morning';
    if (currentHour < 17) return 'Good Afternoon';
    return 'Good Evening';
  }, []);

  /**
   * The class notes, which are the daily report.
   *
   * Written against `daily_reports` rather than a table of their own: what a
   * teacher covered in one class on one day is already a row keyed on exactly
   * that — section, subject and date — and a second table holding the same
   * three columns would be a second answer to the same question (CLAUDE.md §3).
   *
   * The consequence is worth being plain about on screen, and the modal below
   * says it: this is the institute's record of the lesson, not a private note.
   * The full page for it is `/teacher/daily-report`; this card is the same rows
   * for one day, reachable from the period they belong to.
   */
  const [notesDateFilter, setNotesDateFilter] = useState<string>('');
  const notesDate = notesDateFilter || overviewDateFilter || todayDateString;

  const {
    data: reportDay,
    isLoading: isLoadingNotes,
    refetch: refetchNotes,
  } = useApiQuery(
    () =>
      canHoldClasses && activeTab === 'overview'
        ? dailyReportsApi.day(notesDate)
        : Promise.resolve(null),
    [canHoldClasses, activeTab, notesDate],
  );

  /** Only the classes actually written up — the card lists reports, not periods. */
  const filteredNotesList = useMemo(
    () =>
      (reportDay?.classes ?? [])
        .filter((entry) => entry.report !== null)
        .map((entry) => ({
          id: `${entry.classSectionUuid}:${entry.subject.uuid}`,
          classSectionUuid: entry.classSectionUuid,
          subjectUuid: entry.subject.uuid,
          date: reportDay?.date ?? notesDate,
          subject: entry.subject.name,
          className: entry.displayName,
          // The office fills a room in when it arranges the week; until then the
          // period is still a period.
          room: entry.periods[0]?.roomName ?? 'Room not set',
          content: entry.report?.content ?? '',
          createdAt: formatInstituteDateTime(entry.report?.savedAt ?? ''),
        })),
    [reportDay, notesDate],
  );

  /**
   * Which of the day's lessons already have a report, for the dot on the note
   * button. Only meaningful while the card is showing the same day the schedule
   * is — a teacher looking back at last Tuesday's reports is not being told
   * about today's periods.
   */
  const writtenUpToday = useMemo(
    () =>
      new Set(
        notesDate === overviewDateFilter
          ? (reportDay?.classes ?? [])
              .filter((entry) => entry.report !== null)
              .map((entry) => `${entry.classSectionUuid}:${entry.subject.uuid}`)
          : [],
      ),
    [reportDay, notesDate, overviewDateFilter],
  );

  const formattedNotesDate = useMemo(
    () =>
      new Date(`${notesDate}T00:00:00`).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      }),
    [notesDate],
  );

  const [activeNotesItem, setActiveNotesItem] = useState<{
    classSectionUuid: string;
    subjectUuid: string;
    subject: string;
    class: string;
    room: string;
    date: string;
  } | null>(null);
  const [currentNoteText, setCurrentNoteText] = useState<string>('');
  const [isSavingNote, setIsSavingNote] = useState(false);

  /**
   * Opens the report for one class-and-subject on one day.
   *
   * The saved text is read from what the server already sent for that day, so
   * the box opens on what is stored rather than on an empty draft that would
   * overwrite it.
   */
  const openNotesModal = (item: {
    classSectionUuid: string;
    subjectUuid: string;
    subject: string;
    class: string;
    room: string;
    date?: string;
  }) => {
    const targetDate = item.date || notesDate;
    const existing = (reportDay?.classes ?? []).find(
      (entry) =>
        entry.classSectionUuid === item.classSectionUuid &&
        entry.subject.uuid === item.subjectUuid,
    );

    setActiveNotesItem({ ...item, date: targetDate });
    setCurrentNoteText(existing?.report?.content ?? '');
  };

  /** Saves the report, or withdraws it when the box has been cleared. */
  const saveNote = async () => {
    if (!activeNotesItem) return;
    setIsSavingNote(true);

    // This box edits the topic alone. Only the topic is sent: the server leaves
    // every field it is not sent as stored, so homework, remarks, methods and
    // files written on the full form survive — and an older copy held in this
    // tab can never overwrite a newer edit made elsewhere.
    try {
      const saved = await dailyReportsApi.save({
        classSectionUuid: activeNotesItem.classSectionUuid,
        subjectUuid: activeNotesItem.subjectUuid,
        reportDate: activeNotesItem.date,
        content: currentNoteText.trim(),
      });

      toast.success(
        saved ? 'Report saved' : 'Report withdrawn',
        `${activeNotesItem.class} · ${activeNotesItem.subject}`,
      );
      setActiveNotesItem(null);
      refetchNotes();
    } catch (caught) {
      // The server names the reason — a subject this class does not take, a
      // date outside the session — so it is shown as it stands.
      toast.error(
        'Could not save this report',
        caught instanceof ApiRequestError
          ? caught.message
          : 'Something went wrong. Please try again.',
      );
    } finally {
      setIsSavingNote(false);
    }
  };

  /**
   * The day's figures for each section, keyed by the label the filter uses.
   *
   * One entry per section rather than per allotment: a teacher who takes two
   * subjects in 8-A is looking at one register and one video library.
   */
  const sectionStats = useMemo(
    () => new Map((overview?.sections ?? []).map((section) => [section.displayName, section])),
    [overview],
  );

  /** The week's periods per section, for the class list — never a made-up count. */
  const periodsBySection = useMemo(
    () =>
      Object.fromEntries(
        (overview?.sections ?? []).map((section) => [
          section.classSectionUuid,
          section.periodsThisWeek,
        ]),
      ),
    [overview],
  );

  /**
   * Every allotment in the section the filter names — one per subject taught
   * there. Reading only the first named one subject when the teacher takes two.
   */
  const selectedOverviewAllotments = allocatedClasses.filter(
    (c) => c.className === overviewClassFilter,
  );
  const selectedOverviewClass = selectedOverviewAllotments[0];
  const selectedOverviewSubjects = [
    ...new Set(selectedOverviewAllotments.flatMap((c) => (c.subject ? [c.subject] : []))),
  ];
  const selectedOverviewRole = selectedOverviewAllotments.some((c) => c.role === 'Class Teacher')
    ? 'Class Teacher'
    : 'Subject Teacher';

  /**
   * Sections, counted once each. A teacher who takes two subjects in 8-A holds
   * two allotments for it, and counting allotments read as two classes and
   * twice 8-A's children.
   */
  const allSections = distinctSections(allocatedClasses);
  const studentsBySection = new Map(
    allocatedClasses.map((c) => [c.classSectionUuid, c.totalStudents]),
  );

  /** The sections the two counting cards below are totalling — all, or the one chosen. */
  const countedSections = useMemo(() => {
    const sections = overview?.sections ?? [];
    if (overviewClassFilter === 'all') return sections;

    const chosen = sectionStats.get(overviewClassFilter);
    return chosen ? [chosen] : [];
  }, [overview, overviewClassFilter, sectionStats]);

  const formattedOverviewDate = new Date(overviewDateFilter + 'T00:00:00').toLocaleDateString(
    'en-US',
    {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    },
  );

  /**
   * Nothing has arrived yet.
   *
   * Only the very first load: a change of date keeps the day before on screen
   * until the new one lands, rather than emptying every card in between.
   */
  const isFirstLoad = isLoadingOverview && !overview;
  /** What a figure reads as before it is known. Never a zero, which is an answer. */
  const PENDING = '—';

  // 1. Allocated Classes Card
  const periodsThisWeek = overview?.periodsThisWeek ?? 0;
  let allocatedCardTitle = `${allSections.length} ${allSections.length === 1 ? 'Class' : 'Classes'}`;
  let allocatedCardSubtext = isFirstLoad
    ? `${PENDING} Periods / Week`
    : periodsThisWeek === 1
      ? '1 Period / Week'
      : `${periodsThisWeek} Periods / Week`;
  if (overviewClassFilter !== 'all') {
    if (selectedOverviewClass) {
      allocatedCardTitle = selectedOverviewClass.className;
      allocatedCardSubtext = [...selectedOverviewSubjects, selectedOverviewRole].join(' · ');
    } else {
      allocatedCardTitle = 'No Classes';
      allocatedCardSubtext = 'No allocated class found';
    }
  }

  // 2. Total Students Card
  let studentsCardTitle = '0';
  let studentsCardSubtext = 'No enrollment data available';
  if (overviewClassFilter === 'all') {
    const totalCount = [...studentsBySection.values()].reduce((sum, count) => sum + count, 0);
    studentsCardTitle = `${totalCount}`;
    studentsCardSubtext = `Across ${allSections.length} ${allSections.length === 1 ? 'class' : 'classes'}`;
  } else if (selectedOverviewClass) {
    studentsCardTitle = `${selectedOverviewClass.totalStudents}`;
    studentsCardSubtext = `Section ${selectedOverviewClass.section}`;
  }

  /** The day the date-led cards are about, at the start of each one's line. */
  const dateLabel = overviewDateFilter === todayDateString ? 'Today' : formattedOverviewDate;

  // 3. Class Attendance Card (Percentage + Present & Absent counts + Fallback)
  //
  // Counted over the registers actually taken, never over the roster: a section
  // nobody has marked yet would otherwise drag the figure down as though every
  // child in it were absent. A late child was in class, so late counts as
  // attended — the same rule the daily report's attendance card uses, so the
  // two screens never disagree about the same register.
  const takenRegisters = countedSections.filter((section) => section.attendance.isMarked);
  const attendedCount = takenRegisters.reduce(
    (sum, s) => sum + s.attendance.present + s.attendance.late,
    0,
  );
  const absentCount = takenRegisters.reduce((sum, s) => sum + s.attendance.absent, 0);
  const markedCount = takenRegisters.reduce(
    (sum, s) => sum + s.attendance.present + s.attendance.absent + s.attendance.late + s.attendance.leave,
    0,
  );

  let attendanceCardTitle = isFirstLoad ? PENDING : '-';
  let attendanceCardSubtext = isFirstLoad ? '' : 'No attendance recorded for this date';

  if (markedCount > 0) {
    attendanceCardTitle = `${((attendedCount / markedCount) * 100).toFixed(1)}%`;
    attendanceCardSubtext = `${dateLabel} · ${attendedCount} Attended · ${absentCount} Absent`;
  }

  // 4. Pending Homework Card — children yet to hand in the homework set or due
  // on the chosen day, so it moves with the date filter like the cards beside it.
  const unsubmittedStudents = countedSections.reduce(
    (sum, section) => sum + (section.homework.unsubmittedStudents ?? 0),
    0,
  );

  // 5. Recorded Classes Card — the big count is what was published on the chosen
  // day, so it moves with the date filter; the library's total is the subtext.
  const videosOnDate = countedSections.reduce((sum, s) => sum + s.recordings.onDate, 0);
  const videosTotal = countedSections.reduce((sum, s) => sum + s.recordings.total, 0);

  let videosCardTitle = isFirstLoad ? PENDING : '0 Videos';
  let videosCardSubtext = isFirstLoad ? '' : 'No recorded classes uploaded';

  if (videosTotal > 0) {
    videosCardTitle = `${videosOnDate} ${videosOnDate === 1 ? 'Video' : 'Videos'}`;
    videosCardSubtext = `${dateLabel} · ${videosTotal} Total ${videosTotal === 1 ? 'Video' : 'Videos'}`;
  }

  const handleQuickAction = (
    tab: 'attendance' | 'homework' | 'videos' | 'marks',
    itemClass?: string,
  ) => {
    if (itemClass) {
      const matched = allocatedClasses.find((c) => c.className === itemClass);
      if (matched) {
        setSelectedClassId(matched.id);
      }
    }
    setActiveTab(tab);
  };

  /** Where the day being looked at sits relative to today, as the server sees it. */
  const dayState: DayState = overview?.schedule.isToday
    ? 'today'
    : overviewDateFilter < todayDateString
      ? 'past'
      : 'future';

  // Minutes since midnight at the school, read once per render. Only a period on
  // today's date is decided by it, so a clock that drifts a minute costs nothing
  // — but a device in another zone would be hours out, hence IST.
  const minutesNow = instituteMinutesNow();

  /**
   * The day's periods, as the timetable holds them.
   *
   * The same slots the Class Timetable tab shows, so a period excused there is
   * a period marked unavailable here rather than one this screen still calls
   * "in progress".
   */
  const filteredSchedule = (overview?.schedule.slots ?? [])
    .filter(
      (slot) => overviewClassFilter === 'all' || slot.displayName === overviewClassFilter,
    )
    .map((slot) => ({
      key: `${slot.uuid}-${slot.date}`,
      // Carried through so the note button can write the report against the
      // right section and subject rather than against their printed names.
      classSectionUuid: slot.classSectionUuid,
      subjectUuid: slot.subject.uuid,
      // The institute's own number for the period, not this list's position: a
      // teacher whose first class is period 3 should read "P3".
      periodNumber: slot.period.periodNumber,
      time: periodRange(slot.period.startTime, slot.period.endTime),
      subject: slot.subject.name,
      class: slot.displayName,
      // The office fills a room in when it arranges the week; until then the
      // period is still a period.
      room: slot.roomName ?? 'Room not set',
      status: slotStatus(slot, dayState, minutesNow),
    }));

  // The section being drawn, named as the sidebar names it — the page titles
  // itself after the link that reached it.
  const activeSection = sections.find((section) => section.key === activeTab) ?? sections[0];

  // Below every hook, not above them: an early return that sits among the
  // `useState` calls changes how many run between renders, which is the one
  // thing React's hook order cannot survive.
  if (!user) return null;

  // Redirect Super Admin / Support / Institute Admin to their dedicated consoles
  if (
    user.role === ROLES.SUPER_ADMIN ||
    user.role === ROLES.PLATFORM_SUPPORT ||
    user.role === ROLES.ADMIN
  ) {
    return <Navigate to={ROLE_HOME[user.role]} replace />;
  }

  return (
    <>
      {activeTab === 'homework' && currentTaughtClass ? (
        <div id="homework-page-header" className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 scroll-mt-20">
          {/* Left: Homework heading, Date & Students Enrolled Badge */}
          <div className="flex flex-col gap-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-ink">
              {navLabel(activeSection, user?.featureNames)}
            </h1>
            <div className="flex items-center gap-2.5 flex-wrap">
              <span className="text-xs sm:text-sm font-medium text-ink-3">
                {formatInstituteLongDate(todayDateString)}
              </span>
             
            </div>
          </div>

          {/* Right: Search Bar + Active Class & Subject Dropdown + Post Homework Button */}
          <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            {/* Search Input before the dropdown */}
            <div className="relative w-full sm:w-48 md:w-56">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-ink-3" />
              <input
                type="text"
                value={homeworkSearchQuery}
                onChange={(e) => setHomeworkSearch({ search: e.target.value })}
                placeholder="Search homework..."
                className="h-9 w-full pl-8 pr-2.5 text-sm bg-surface-2 border border-2  rounded-field text-ink placeholder:text-sm placeholder:text-ink-3 focus:outline-none focus:border-indigo-500 transition-colors"
              />
            </div>

            {homeworkClassOptions.length > 0 && (
              <div className="w-full sm:w-64 md:w-72">
                <Select
                  hideLabel
                  label="Active Class & Subject"
                  options={homeworkClassOptions}
                  value={selectedClassId || currentTaughtClass.id}
                  onChange={(e) => setSelectedClassId(e.target.value)}
                  className="h-9 text-sm font-semibold rounded-field"
                />
              </div>
            )}

            <Button
              variant={isHomeworkFormOpen ? 'secondary' : 'primary'}
              size="sm"
              leftIcon={isHomeworkFormOpen ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
              onClick={() => setIsHomeworkFormOpen((open) => !open)}
              className={`h-9 px-4 text-[14px] font-bold shadow-xs rounded-[10px]
                        ${
                          isHomeworkFormOpen
                            ? 'bg-red-600 text-white hover:bg-red-700 hover:text-white hover:border-red-700 border-red-600'
                            : '  bg-indigo-600 hover:bg-indigo-700 hover:text-white text-white'
                        }`}
            >
              {isHomeworkFormOpen ? 'Close' : 'Post Homework'}
            </Button>
          </div>
        </div>
      ) : (
        activeTab !== 'overview' &&
        activeTab !== 'attendance' &&
        activeTab !== 'activities' &&
        activeTab !== 'videos' &&
        activeTab !== 'timetable' &&
        activeTab !== 'marks' && (
          <PageHeader
            title={navLabel(activeSection, user?.featureNames)}
          />
        )
      )}

      <div className="flex flex-col gap-6 print:block print:gap-0">
        {/* Every screen below is drawn from the allotment, so all four of
            its states are handled here rather than seven times over. */}
        {isLoadingClasses && (
          <div className="card p-6">
            <SkeletonLines count={8} />
          </div>
        )}

        {!isLoadingClasses && classesError && (
          <div className="card flex flex-col items-start gap-3 p-6">
            <Alert tone="critical" title="Could not load your classes">
              {classesError.message}
            </Alert>
            <Button variant="secondary" size="sm" onClick={refetchClasses}>
              Try again
            </Button>
          </div>
        )}

        {!isLoadingClasses && !classesError && (
          <>
            {/* Class & Subject Selector Header */}
            {activeTab !== 'overview' && activeTab !== 'attendance' && activeTab !== 'activities' && activeTab !== 'videos' && activeTab !== 'marks' && activeTab !== 'homework' && allocatedClasses.length > 0 && (
              <ClassSelectorHeader
                teacherName={`${user.firstName} ${user.lastName}`}
                email={user.email}
                allocatedClasses={allocatedClasses}
                selectedClassId={selectedClassId}
                onSelectClass={setSelectedClassId}
              />
            )}

            {/* ---------------- Tab Contents ---------------- */}
            {activeTab === 'overview' && (
              <div className="flex flex-col gap-6">
                {/* Greeting Header Row & Top Filters */}
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold text-ink flex items-center gap-2">
                      {greeting}, {user.firstName}! <span className="inline-block animate-pulse">👋</span>
                    </h1>
                    <p className="text-xs sm:text-sm text-ink-3 font-medium mt-1">
                      {overviewDateFilter === todayDateString
                        ? "Here's what's happening in your classes today."
                        : `Here's your classes on ${formattedOverviewDate}.`}
                    </p>
                  </div>

                  {/* Filter Controls (Class dropdown + Date picker) */}
                  <div className="grid grid-cols-1 sm:flex sm:flex-wrap sm:items-center gap-2 sm:gap-2.5 w-full sm:w-auto">
                    {/* Class Selector Dropdown */}
                    <div className="relative w-full sm:w-44">
                      <Select
                        label="Filter the overview by class"
                        hideLabel
                        options={overviewClassFilterOptions}
                        value={overviewClassFilter}
                        onChange={(e) => setOverviewClassFilter(e.target.value)}
                        className="h-9 w-full sm:w-44 text-xs font-semibold rounded-[10px]"
                      />
                    </div>

                    {/* Date Picker */}
                    <div className="flex items-center gap-1.5 w-full sm:w-auto">
                      <div className="flex-1 sm:w-36">
                        <DatePicker
                          label="Filter the overview by date"
                          hideLabel
                          size="sm"
                          value={overviewDateFilter}
                          onChange={(e) => setOverviewDateFilter(e.target.value || todayDateString)}
                          className="h-9 w-full sm:w-36 text-[14px] font-semibold rounded-[10px] focus:ring-0 focus:outline-none"
                        />
                      </div>
                      {overviewDateFilter !== todayDateString && (
                        <button
                          type="button"
                          title="Reset date"
                          onClick={() => {
                            setOverviewDateFilter(todayDateString);
                            setOverviewClassFilter('all');
                          }}
                          className="h-9 px-2 rounded-[10px] border border-line bg-surface text-ink-3 hover:text-ink transition-colors flex items-center justify-center shadow-2xs shrink-0"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      )}
                    </div>
                  </div>
                </div>

                {/* The day's figures failed to load */}
                {overviewError && (
                  <div className="card flex flex-col items-start gap-3 p-4">
                    <Alert tone="critical" title="Could not load this day's figures">
                      {overviewError.message}
                    </Alert>
                    <Button variant="secondary" size="sm" onClick={refetchOverview}>
                      Try again
                    </Button>
                  </div>
                )}

                {/* 5-Column Stats Row with Admin Panel Theme Variants */}
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 w-full max-w-full">
                  <StatCard
                    label="Allocated Classes"
                    value={allocatedCardTitle}
                    hint={allocatedCardSubtext}
                    variant="indigo"
                    icon={<Users className="h-5 w-5 text-white" />}
                    isLoading={isLoadingOverview}
                  />

                  <StatCard
                    label="Total Students"
                    value={studentsCardTitle}
                    hint={studentsCardSubtext}
                    variant="cyan"
                    icon={<Users className="h-5 w-5 text-white" />}
                    isLoading={isLoadingOverview}
                  />

                  <StatCard
                    label="Class Attendance"
                    value={attendanceCardTitle}
                    hint={attendanceCardSubtext}
                    variant="emerald"
                    icon={<CheckCircle2 className="h-5 w-5 text-white" />}
                    isLoading={isLoadingOverview}
                  />

                  <StatCard
                    label="Pending Homework"
                    value={isFirstLoad ? PENDING : String(unsubmittedStudents)}
                    hint={
                      isFirstLoad
                        ? ''
                        : unsubmittedStudents === 0
                          ? `${dateLabel} · Nothing pending`
                          : `${dateLabel} · ${unsubmittedStudents} ${unsubmittedStudents === 1 ? 'student has' : 'students have'} not submitted`
                    }
                    hintTitle={
                      isFirstLoad
                        ? undefined
                        : unsubmittedStudents === 0
                          ? 'No student is short of homework set or due on this day'
                          : `${unsubmittedStudents} ${unsubmittedStudents === 1 ? 'student has' : 'students have'} not submitted homework set or due on this day`
                    }
                    variant="amber"
                    icon={<FileText className="h-5 w-5 text-white" />}
                    isLoading={isLoadingOverview}
                  />

                  <StatCard
                    label="Recorded Classes"
                    value={videosCardTitle}
                    hint={videosCardSubtext}
                    variant="rose"
                    icon={<Video className="h-5 w-5 text-white" />}
                    isLoading={isLoadingOverview}
                  />
                </div>

                {/* Main Schedule & Overview Layout Grid (Left 7-Cols / Right 5-Cols) */}
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-5 items-start">
                  {/* Left Column (7-Cols): Today's Schedule + My Attendance */}
                  <div className="lg:col-span-7 flex flex-col gap-6">
                    {/* 1. Today's Schedule Card */}
                    <div className="card overflow-hidden bg-surface border border-line rounded-[16px] shadow-2xs">
                      <div className="flex items-center justify-between border-b border-line bg-surface px-5 py-3.5">
                        <div className="flex items-center gap-2.5">
                          <span className="h-5 w-1 rounded-full bg-indigo-600" />
                          <h2 className="text-base font-bold text-ink tracking-tight">
                            {overviewDateFilter === todayDateString ? "Today's Schedule" : 'Schedule'}{' '}
                            ({formattedOverviewDate})
                          </h2>
                        </div>
                        <span className="text-xs text-indigo-600 dark:text-indigo-400 font-bold bg-indigo-50 dark:bg-indigo-500/10 px-2.5 py-0.5 rounded-pill border border-indigo-200 dark:border-indigo-500/20">
                          {filteredSchedule.length}{' '}
                          {filteredSchedule.length === 1 ? 'Period' : 'Periods'}
                        </span>
                      </div>

                      <div className="p-4 flex flex-col gap-3">
                        {isLoadingOverview ? (
                          <SkeletonLines count={4} />
                        ) : filteredSchedule.length === 0 ? (
                          <div className="p-8 text-center text-ink-3 text-xs font-medium">
                            No periods scheduled for{' '}
                            {overviewClassFilter === 'all' ? 'your classes' : overviewClassFilter}{' '}
                            on {formattedOverviewDate}.
                          </div>
                        ) : (
                          filteredSchedule.map((item) => (
                            <div
                              key={item.key}
                              className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-2.5 sm:p-2 rounded-[12px] border border-line bg-surface hover:bg-slate-50/70 dark:hover:bg-surface-2 hover:border-indigo-400/40 transition-all"
                            >
                              <div className="flex items-start sm:items-center gap-3 min-w-0">
                                <span className="grid h-10 w-10 shrink-0 place-items-center rounded-[10px] bg-blue-600 text-white font-bold text-xs shadow-xs mt-0.5 sm:mt-0">
                                  P{item.periodNumber}
                                </span>
                                <div className="min-w-0 flex-1">
                                  <div className="flex flex-wrap items-baseline gap-1 sm:gap-1.5">
                                    <h3 className="text-[14px] font-bold text-ink">
                                      {item.subject}
                                    </h3>
                                    <span className="text-xs text-ink-3 font-semibold">
                                      ({item.class})
                                    </span>
                                  </div>
                                  <div className="text-xs sm:text-sm text-ink-3 mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 font-medium">
                                    <span className="inline-flex items-center gap-1 shrink-0">
                                      <Clock className="h-3 w-3 text-indigo-500 shrink-0" /> {item.time}
                                    </span>
                                    <span className="text-ink-4 hidden sm:inline">·</span>
                                    <span className="inline-flex items-center gap-1 shrink-0">
                                      <MapPin className="h-3 w-3 text-ink-3 shrink-0" /> {item.room}
                                    </span>
                                  </div>
                                </div>
                              </div>

                              <div className="flex items-center justify-between sm:justify-end gap-2.5 w-full sm:w-auto border-t sm:border-t-0 border-line/60 pt-2 sm:pt-0">
                                <div className="flex items-center gap-1.5">
                                  <button
                                    type="button"
                                    title="Mark Attendance"
                                    onClick={() => handleQuickAction('attendance', item.class)}
                                    className="group grid h-7 w-7 place-items-center rounded-full border border-indigo-500/20 bg-indigo-500/10 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-all shadow-2xs hover:scale-105"
                                  >
                                    <CheckCircle2 className="h-3.5 w-3.5 transition-colors group-hover:text-white" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Set assignment"
                                    onClick={() => handleQuickAction('homework', item.class)}
                                    className="group grid h-7 w-7 place-items-center rounded-full border border-blue-500/20 bg-blue-500/10 text-blue-600 hover:bg-blue-600 hover:text-white transition-all shadow-2xs hover:scale-105"
                                  >
                                    <FileText className="h-3.5 w-3.5 transition-colors group-hover:text-white" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Upload Video"
                                    onClick={() => handleQuickAction('videos', item.class)}
                                    className="group grid h-7 w-7 place-items-center rounded-full border border-purple-500/20 bg-purple-500/10 text-purple-600 hover:bg-purple-600 hover:text-white transition-all shadow-2xs hover:scale-105"
                                  >
                                    <Video className="h-3.5 w-3.5 transition-colors group-hover:text-white" />
                                  </button>
                                  <button
                                    type="button"
                                    title="Write up this lesson"
                                    onClick={() =>
                                      openNotesModal({
                                        classSectionUuid: item.classSectionUuid,
                                        subjectUuid: item.subjectUuid,
                                        subject: item.subject,
                                        class: item.class,
                                        room: item.room,
                                        date: overviewDateFilter,
                                      })
                                    }
                                    className="relative grid h-7 w-7 place-items-center rounded-full border border-amber-500/20 bg-amber-500/10 text-amber-500 hover:bg-amber-500/20 hover:text-amber-400 transition-all shadow-2xs hover:scale-105"
                                  >
                                    <StickyNote className="h-3.5 w-3.5 text-amber-500 transition-colors" />
                                    {writtenUpToday.has(
                                      `${item.classSectionUuid}:${item.subjectUuid}`,
                                    ) && (
                                      <span
                                        className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-amber-500 ring-2 ring-surface"
                                        title="Written up"
                                      />
                                    )}
                                  </button>
                                </div>

                                <span
                                  className={`rounded-pill border px-2.5 py-0.5 text-2xs font-bold shrink-0 ${
                                    item.status === 'In Progress'
                                      ? 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-500/15 dark:text-emerald-400 dark:border-emerald-500/30'
                                      : item.status === 'Completed'
                                        ? 'bg-slate-100 text-slate-600 border-slate-200 dark:bg-slate-800 dark:text-slate-400 dark:border-slate-700'
                                        : 'bg-indigo-50 text-indigo-700 border-indigo-200 dark:bg-indigo-500/15 dark:text-indigo-400 dark:border-indigo-500/30'
                                  }`}
                                >
                                  {item.status}
                                </span>
                              </div>
                            </div>
                          ))
                        )}
                      </div>

                      {/* View Full Timetable button */}
                      <div className="border-t border-line py-2.5 text-center bg-surface">
                        <button
                          type="button"
                          onClick={() => setActiveTab('timetable')}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-blue-600 dark:text-blue-400 hover:text-blue-700 transition-colors"
                        >
                          <Calendar className="h-3.5 w-3.5" />
                          View Full Timetable
                        </button>
                      </div>
                    </div>

                    {/* 2. My Attendance Card */}
                    <TeacherAttendanceAnalyticsCard />

                    {/* 3. Class Performance Card */}
                    <BestPerformersCard
                      items={overview?.performance ?? []}
                      isLoading={isLoadingOverview}
                    />
                  </div>

                  {/* Right Column (5-Cols): My Classes, My Class Notes, My Class Notices, Institute Notices */}
                  <div className="lg:col-span-5 flex flex-col gap-6">
                    {/* My Classes Card */}
                    <MyClassesCard
                      classes={allocatedClasses}
                      periodsBySection={periodsBySection}
                      onSelectClass={(id) => {
                        setSelectedClassId(id);
                        setActiveTab('attendance');
                      }}
                      // The full list of allotments lives with the timetable,
                      // where each one's periods are.
                      onViewAll={() => navigate('/teacher/timetable', { state: { scrollTo: 'allocated-classes' } })}
                    />

                    {/* Teacher Class Notes Card */}
                    <TeacherClassNotesCard
                      notesDateFilter={notesDateFilter}
                      setNotesDateFilter={setNotesDateFilter}
                      filteredNotesList={filteredNotesList}
                      formattedNotesDate={formattedNotesDate}
                      isLoading={isLoadingNotes}
                      onEditNote={(note) =>
                        openNotesModal({
                          classSectionUuid: note.classSectionUuid,
                          subjectUuid: note.subjectUuid,
                          subject: note.subject,
                          class: note.className,
                          room: note.room,
                          date: note.date,
                        })
                      }
                    />

                    {/* Teacher-Created Class Notices */}
                    <TeacherNoticesCard />

                    {/* Institute Notices Feed */}
                    <InstituteNoticesCard />

                    {/* Student Progress Card */}
                    <StudentProgressCard
                      progress={overview?.progress ?? { thisMonth: [], thisTerm: [] }}
                      date={overview?.date}
                      isLoading={isLoadingOverview}
                    />
                  </div>
                </div>

                {/* Bottom Full-Width Banner: Teaching Tip of the Day */}
                {showTeachingTip && (
                  <div className="card bg-surface border border-line rounded-[16px] p-4 flex items-center justify-between gap-4 shadow-2xs">
                    <div className="flex items-center gap-3.5">
                      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-purple-500/15 text-purple-400 border border-purple-500/25">
                        <Lightbulb className="h-5 w-5" />
                      </span>
                      <div>
                        <h4 className="text-sm font-bold text-purple-400">Teaching Tip of the Day</h4>
                        <p className="text-xs text-ink-2 font-medium mt-0.5">
                          {teachingTip.source
                            ? `“${teachingTip.text}” — ${teachingTip.source}`
                            : teachingTip.text}
                        </p>
                      </div>
                    </div>
                    <button
                      type="button"
                      onClick={() => setShowTeachingTip(false)}
                      className="text-ink-3 hover:text-ink transition-colors p-1"
                      title="Dismiss"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                )}
              </div>
            )}

            {activeTab === 'attendance' && (
              <TeacherAttendanceSection
                currentClass={currentClass}
                allocatedClasses={allocatedClasses}
                selectedClassId={selectedClassId}
                onSelectClass={setSelectedClassId}
              />
            )}
            {SUBJECT_TABS.includes(activeTab) && !currentTaughtClass && allocatedClasses.length > 0 && (
              <EmptyState
                title="No subject to teach yet"
                description="You are a class teacher, but no subject has been allotted to you. Homework, marks, videos and activities open once the school office allots you a subject."
              />
            )}
            {activeTab === 'homework' && currentTaughtClass && (
              <TeacherHomeworkSection
                currentClass={currentTaughtClass}
                isFormOpen={isHomeworkFormOpen}
                setIsFormOpen={setIsHomeworkFormOpen}
                searchQuery={homeworkSearchQuery}
              />
            )}
            {activeTab === 'activities' && currentTaughtClass && (
              <TeacherActivitiesSection
                currentClass={currentTaughtClass}
                allocatedClasses={taughtClasses}
                selectedClassId={selectedClassId}
                onSelectClass={setSelectedClassId}
              />
            )}
            {activeTab === 'videos' && currentTaughtClass && (
              <TeacherVideosSection
                currentClass={currentTaughtClass}
                allocatedClasses={taughtClasses}
                selectedClassId={selectedClassId}
                onSelectClass={setSelectedClassId}
              />
            )}
            {activeTab === 'timetable' && (
              <TeacherTimetableSection
                allocatedClasses={allocatedClasses}
                selectedClassId={selectedClassId}
                onSelectClass={setSelectedClassId}
              />
            )}
            {activeTab === 'marks' && currentTaughtClass && (
              <TeacherMarksSection
                currentClass={currentTaughtClass}
                allocatedClasses={taughtClasses}
                classTeacherSections={classTeacherSections}
              />
            )}
          </>
        )}
      </div>

      {/* Class Teaching Notes Modal */}
      {activeNotesItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-xs animate-in fade-in duration-150">
          <div className="card w-full max-w-lg overflow-hidden bg-surface border border-line rounded-[20px] shadow-2xl">
            {/* Modal Header */}
            <div className="flex items-center justify-between border-b border-line bg-surface-2 px-6 py-4">
              <div className="flex items-center gap-3">
                <span className="grid h-9 w-9 place-items-center rounded-[10px] bg-amber-500/15 text-amber-600 border border-amber-500/20">
                  <StickyNote className="h-5 w-5" />
                </span>
                <div>
                  <h3 className="text-base font-bold text-ink">Daily report</h3>
                  <p className="text-2xs text-ink-3 font-semibold">
                    {activeNotesItem.subject} ({activeNotesItem.class}) · {activeNotesItem.room}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveNotesItem(null)}
                className="grid h-8 w-8 place-items-center rounded-full text-ink-3 hover:bg-surface hover:text-ink transition-colors"
              >
                <X className="h-4.5 w-4.5" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-6 flex flex-col gap-4">
              {/* Said plainly, because it decides what a teacher writes here:
                  this is the institute's record of the lesson, the same row the
                  Daily report screen shows — not a private note. */}
              <div className="flex items-center justify-between gap-2 text-xs text-ink-3 font-semibold">
                <span>What you covered in this class</span>
                <span className="shrink-0 text-2xs text-amber-600 font-bold bg-amber-500/10 px-2 py-0.5 rounded-pill border border-amber-500/20">
                  {formatInstituteDate(activeNotesItem.date)}
                </span>
              </div>

              <textarea
                rows={5}
                maxLength={2000}
                value={currentNoteText}
                onChange={(e) => setCurrentNoteText(e.target.value)}
                placeholder="Syllabus covered, activities, tests set…"
                className="w-full rounded-[12px] bg-surface-2 p-4 text-sm font-medium text-ink placeholder:text-ink-3 border border-line focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20 transition-all resize-none"
              />

              <p className="text-2xs text-ink-3 font-medium">
                Saved to your daily report, where the next teacher and the school office can
                see it. Clearing the box withdraws it.
              </p>

              <div className="flex items-center justify-end gap-3 pt-2">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={isSavingNote}
                  onClick={() => setActiveNotesItem(null)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  isLoading={isSavingNote}
                  onClick={() => void saveNote()}
                >
                  Save
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
